import { Head, Link, router, usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

type ChecklistTemplateSummary = {
  readonly id: number
  readonly name: string
  readonly items: readonly { readonly id: number }[]
  readonly tasks_count: number
}

type ChecklistTemplatesIndexProps = {
  readonly checklist_templates: readonly ChecklistTemplateSummary[]
}

export default function ChecklistTemplatesIndex({
  checklist_templates,
}: ChecklistTemplatesIndexProps) {
  const { props } = usePage<PageProps<ChecklistTemplatesIndexProps>>()
  const description = "Manage reusable checklist templates that apply a set of onboarding tasks to a client."

  return (
    <>
      <Head title="Checklist templates">
        <meta name="description" content={description} />
        <meta property="og:title" content="Checklist templates" />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>Checklist templates</h1>
              <p className="mt-2">Reusable task lists you can apply to a client in one click.</p>
            </div>
            <Button asChild>
              <Link href="/settings/checklist_templates/new">
                <Plus className="h-4 w-4" />
                New template
              </Link>
            </Button>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          {checklist_templates.length > 0 ? (
            <section className="overflow-hidden rounded-lg border border-hairline bg-page">
              <div className="divide-y divide-hairline">
                {checklist_templates.map((template) => (
                  <article
                    key={template.id}
                    className="flex items-center justify-between gap-4 px-4 py-4"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-ink-display">{template.name}</div>
                      <div className="mt-1 text-sm text-ink-muted">
                        {template.items.length} {template.items.length === 1 ? "item" : "items"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge tone="muted">{template.tasks_count} tasks created</Badge>
                      <Button asChild variant="secondary" size="sm">
                        <Link href={`/settings/checklist_templates/${template.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(`Delete template "${template.name}"?`)) {
                            router.delete(`/settings/checklist_templates/${template.id}`, {
                              preserveScroll: true,
                            })
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <section className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center">
              <h2>No checklist templates yet</h2>
              <p className="mt-2">Create a template to speed up client onboarding.</p>
            </section>
          )}
        </div>
      </SettingsLayout>
    </>
  )
}
